import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PendingService } from '@mydocma/app-container';
import { NotificationService } from '@mydocma/component-library/src/lib/notification';
import {
  PersonalizationService,
  ScreenOrientationEnum,
  ScreenOrientationService,
} from '@mydocma/core';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  finalize,
  firstValueFrom,
  forkJoin,
  from,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import {
  IShareLinkConfirmationDialogData,
  IShareLinkDialogData,
  ShareLinkSelection,
} from '../models/document-dialog';
import {
  IDocumentExtendedRequest,
  IDocumentExtendedResponse,
  IFilter,
  ISort,
} from '../models/document-extended';
import { IDocument } from '../models/documents';
import { PrConfigAPIResponse } from '../models/pr-config-api';
import { PrErrorCodeEnum } from '../models/pr-error-code';
import { IShareLinkRequest, IShareLinkResponse, ShareAccessTypeEnum } from '../models/share-link';
import { BlockingLoaderService } from './blocking-loader.service';
import { ConfigApiService } from './config-api.service';
import { ErrorHandlingService } from './error-handling.service';
import { ShareLinkApiService } from './share-link-api.service';

@Injectable({
  providedIn: 'root',
})
export class ShareDocumentLinkService {
  private destroy$ = new Subject<void>();
  private currentCustomerId!: string;
  private currentProjectId!: string;

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();

  private readonly detailsLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly detailsLoading$ = this.detailsLoadingSubject.asObservable();

  private readonly showFileSelectionSubject = new BehaviorSubject<boolean>(false);
  readonly showFileSelection$ = this.showFileSelectionSubject.asObservable();

  private readonly shareInfoSubject = new BehaviorSubject<IShareLinkResponse | null>(null);
  readonly shareInfo$ = this.shareInfoSubject.asObservable();

  private readonly extendedMetadataSubject = new BehaviorSubject<IDocumentExtendedResponse[]>([]);
  readonly extendedMetadata$ = this.extendedMetadataSubject.asObservable();

  private readonly documentSubject = new BehaviorSubject<IDocument>(null);
  readonly document$ = this.documentSubject.asObservable();
  private readonly selectedDocumentIdSubject = new BehaviorSubject<string | null>(null);
  private readonly documentSelectionRequestSubject = new Subject<{
    shareInfo: IShareLinkResponse;
    documentId: string;
  }>();

  private readonly documentsExtendedSubject = new BehaviorSubject<IDocumentExtendedResponse[]>([]);
  readonly documentsExtended$ = this.documentsExtendedSubject.asObservable();

  private readonly documentCountSubject = new BehaviorSubject<number>(0);
  readonly documentCount$ = this.documentCountSubject.asObservable();

  private blockingLoadingSubject = new BehaviorSubject<boolean>(false);
  public isBlockingLoading$ = this.blockingLoadingSubject.asObservable();

  private readonly fileBlobRequestCache = new Map<string, Observable<Blob | null>>();

  private readonly refreshSubject = new Subject<IFilter[]>();
  readonly refresh$ = this.refreshSubject.asObservable();

  private readonly cancelPreviousRequest$ = new Subject<void>();

  private lastFilterOptions: IFilter[] = [];
  private currentGroupingFilteringRequest: IDocumentExtendedRequest | null = null;

  private readonly fileOnlySubject = new BehaviorSubject<boolean>(false);
  readonly isFileOnly$ = this.fileOnlySubject.asObservable();

  private isInitialized = false;

  constructor(
    private readonly screenOrientationService: ScreenOrientationService,
    private readonly matDialog: MatDialog,
    private readonly personalizationService: PersonalizationService,
    private readonly configApiService: ConfigApiService,
    private readonly errorHandlingService: ErrorHandlingService,
    private readonly notificationService: NotificationService,
    private readonly translateService: TranslateService,
    private readonly pendingService: PendingService,
    private readonly shareLinkApiService: ShareLinkApiService,
    private readonly blockingLoaderService: BlockingLoaderService,
  ) {
    this.pendingService.registerPendingState(this.blockingLoadingSubject);
    combineLatest([this.shareInfo$, this.refreshSubject])
      .pipe(
        filter(([shareInfo]) => !!shareInfo?.shareId),
        tap(() => {
          this.cancelPreviousRequest$.next();
          this.setLoading(true);
        }),
        switchMap(([shareInfo]) => {
          if (!shareInfo?.shareId) return of({ count: 0, extendedMetadata: [] });
          const request = this.currentGroupingFilteringRequest ?? {
            sorting: [],
            filtering: this.lastFilterOptions,
          };

          const metadata$ =
            shareInfo.shareAccessType === ShareAccessTypeEnum.PUBLIC
              ? this.getSharedExtendedMetadataPublic(
                  shareInfo.customerId,
                  shareInfo.shareId,
                  request,
                )
              : this.getSharedExtendedMetadata(shareInfo.shareId, request);

          const count$ =
            shareInfo.shareAccessType === ShareAccessTypeEnum.PUBLIC
              ? this.getSharedDocumentsCountPublic(shareInfo.customerId, shareInfo.shareId, {
                  filtering: this.lastFilterOptions,
                })
              : this.getSharedDocumentsCount(shareInfo.shareId, {
                  filtering: this.lastFilterOptions,
                });

          return forkJoin({
            count: count$,
            extendedMetadata: metadata$,
          }).pipe(
            tap(() => {
              this.currentGroupingFilteringRequest = null;
            }),
            takeUntil(this.cancelPreviousRequest$),
            catchError(() => {
              this.currentGroupingFilteringRequest = null;
              this.documentsExtendedSubject.next([]);
              return of({ count: 0, extendedMetadata: [] });
            }),
          );
        }),
        tap(({ count, extendedMetadata }) => {
          if (Array.isArray(extendedMetadata) && extendedMetadata.length === 0) {
            this.setLoading(false);
            this.setBlockingLoading(false);
          }
          this.documentCountSubject.next(count);
          this.documentsExtendedSubject.next(extendedMetadata);
        }),
      )
      .subscribe();

    this.documentSelectionRequestSubject
      .pipe(
        switchMap(({ shareInfo, documentId }) => {
          const fetch$ =
            shareInfo.shareAccessType === ShareAccessTypeEnum.PUBLIC
              ? this.getSharedDocumentsMetadataByIdsPublic(
                  shareInfo.customerId,
                  shareInfo.shareId,
                  [documentId],
                )
              : this.getSharedDocumentsMetadataByIds(shareInfo.shareId, [documentId]);

          return fetch$.pipe(
            tap((docs) => {
              if (docs?.[0]) {
                this.selectedDocumentIdSubject.next(documentId);
                this.documentSubject.next(docs[0] as IDocument);
              }
            }),
            catchError(() => of([] as IDocument[])),
          );
        }),
      )
      .subscribe();
  }

  initShareLink(customerId: string, shareId: string): void {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.getShareLinkInfo(customerId, shareId)
      .pipe(
        tap((response: IShareLinkResponse) => {
          this.shareInfoSubject.next(response);
        }),
        switchMap((response: IShareLinkResponse) => {
          if (!response?.shareId) return of([]);

          this.setFileOnly(response?.isSingleFileShare);

          if (response.shareAccessType === ShareAccessTypeEnum.PUBLIC) {
            return this.getSharedExtendedMetadataPublic(response.customerId, response.shareId, {
              filtering: [],
              sorting: [],
            });
          }

          return this.getSharedExtendedMetadata(response.shareId, {
            filtering: [],
            sorting: [],
          });
        }),
        tap((ids: IDocumentExtendedResponse[]) => {
          this.extendedMetadataSubject.next(ids ?? []);
        }),
        catchError((error) => {
          this.errorHandlingService.handle(error, 'Failed to initialize share link');
          return of(null);
        }),
      )
      .subscribe();
  }

  resetShareLink(): void {
    this.isInitialized = false;
    this.shareInfoSubject.next(null);
    this.extendedMetadataSubject.next([]);
    this.documentSubject.next({} as IDocument);
    this.selectedDocumentIdSubject.next(null);
  }

  triggerRefreshWithGrouping(sortingArray: ISort[], filterOptions?: IFilter[]): void {
    this.currentGroupingFilteringRequest = { sorting: sortingArray, filtering: filterOptions };
    this.lastFilterOptions = filterOptions ?? [];
    this.refreshSubject.next(this.lastFilterOptions);
  }

  openShareDialog(
    component: ComponentType<any>,
    data: IShareLinkDialogData,
  ): Observable<ShareLinkSelection> {
    this.setLoading(true);
    return from(this.screenOrientationService.currentScreenOrientation$).pipe(
      takeUntil(this.destroy$),
      take(1),
      switchMap((orientation) => {
        const isPortraitMode: boolean = orientation === ScreenOrientationEnum.portrait;
        const config = this.getShareDialogConfig(isPortraitMode, data);
        return this.matDialog.open(component, config).afterClosed();
      }),
      finalize(() => this.setLoading(false)),
    );
  }

  private getShareDialogConfig(isPortraitMode: boolean, data: IShareLinkDialogData): any {
    return {
      width: '500px',
      autoFocus: false,
      disableClose: false,
      panelClass: ['mydocma-dialog', 'mydocma-dialog-xs-fullscreen', 'mydocma-pr-share-dialog'],
      data: {
        file: data.file,
        url: data.url,
        documents: data.documents,
        fileSelection: data.fileSelection,
      },
    };
  }

  openShareConfirmationDialog(
    component: ComponentType<any>,
    data: IShareLinkConfirmationDialogData,
  ): Observable<void> {
    return from(this.screenOrientationService.currentScreenOrientation$).pipe(
      takeUntil(this.destroy$),
      take(1),
      switchMap((orientation) => {
        const isPortraitMode: boolean = orientation === ScreenOrientationEnum.portrait;
        const config = this.getConfirmationShareDialogConfig(isPortraitMode, data);
        return this.matDialog.open(component, config).afterClosed();
      }),
      finalize(() => this.setLoading(false)),
    );
  }

  private getConfirmationShareDialogConfig(
    isPortraitMode: boolean,
    data: IShareLinkConfirmationDialogData,
  ): any {
    return {
      width: '380px',
      autoFocus: false,
      disableClose: false,
      panelClass: [
        'mydocma-dialog',
        'mydocma-dialog-xs-fullscreen',
        'mydocma-pr-share-confirmation-dialog',
      ],
      data: {
        documents: data.documents,
        shareLinkRequest: data.shareLinkRequest,
        deletedDocumentNames: data.deletedDocumentNames,
      },
    };
  }

  async setLoading(loading: boolean): Promise<void> {
    this.loadingSubject.next(loading);
  }

  setLoadingDetails(loading: boolean): void {
    this.detailsLoadingSubject.next(loading);
  }

  async setBlockingLoading(loading: boolean): Promise<void> {
    this.blockingLoadingSubject.next(loading);
    this.blockingLoaderService.setBlocking(loading);
    const isBlockingPending = await firstValueFrom(this.pendingService.isPending);
    if (!loading && isBlockingPending) {
      this.pendingService.removePendingState(this.blockingLoadingSubject);
    }
  }

  setFileOnly(value: boolean): void {
    this.fileOnlySubject.next(value);
  }

  setDocumentOnRowSelection(document: IDocument): void {
    const shareInfo = this.shareInfoSubject.value;
    const documentId = document?.id;
    if (!shareInfo || !documentId) return;
    this.documentSelectionRequestSubject.next({ shareInfo, documentId });
  }

  showFileSelection(show: boolean): void {
    this.showFileSelectionSubject.next(show);
  }

  async getLastSelection(): Promise<ShareLinkSelection> {
    return await this.personalizationService.getValue(this.getStorageKey());
  }

  async saveLastSelection(selection: ShareLinkSelection): Promise<void> {
    await this.personalizationService.setValue(this.getStorageKey(), selection);
  }

  private getStorageKey(): string {
    return `PR.shareDocumentLink.lastSelection_${this.currentCustomerId}_${this.currentProjectId}`;
  }

  getConfigApi(): Observable<PrConfigAPIResponse> {
    return this.configApiService.getConfig().pipe(
      catchError((error) => {
        return of(null);
      }),
    );
  }

  getShareLinkInfo(customerId: string, shareId: string): Observable<IShareLinkResponse> {
    return this.shareLinkApiService.getShareLinkInfo(customerId, shareId);
  }

  setShareInfo(shareLinkResponse: IShareLinkResponse): void {
    this.shareInfoSubject.next(shareLinkResponse);
  }

  // -------- Authenticated User endpoints -------//
  getShareLink(shareLinkRequest: IShareLinkRequest): Observable<IShareLinkResponse> {
    return this.shareLinkApiService.getShareLink(shareLinkRequest).pipe(
      catchError((error) => {
        if (
          error?.errorCode === PrErrorCodeEnum.PR_SHARED_CREATION_FAILED ||
          error.status === 0 ||
          error.status === 504
        ) {
          this.errorHandlingService.handle(
            error,
            this.translateService.instant('PR.documents.notifications.getLinkError'),
          );
          return of(null);
        }
        return throwError(() => error);
      }),
    );
  }

  getSharedExtendedMetadata(
    shareId: string,
    documentExtendedRequest: IDocumentExtendedRequest,
  ): Observable<IDocumentExtendedResponse[]> {
    return this.shareLinkApiService.getShareExtendedMetadata(shareId, documentExtendedRequest).pipe(
      tap((documents: IDocumentExtendedResponse[]) => {
        this.documentsExtendedSubject.next(documents);
      }),
      catchError((error) => {
        this.errorHandlingService.handle(error, 'Failed to fetch documents');
        this.setBlockingLoading(false);
        this.setLoading(false);
        this.documentsExtendedSubject.next([]);
        return of([]);
      }),
    );
  }

  getSharedDocumentsMetadataByIds(shareId: string, documentIds: string[]): Observable<IDocument[]> {
    return this.shareLinkApiService.getSharedDocumentsMetadataByIds(shareId, documentIds).pipe(
      finalize(() => {
        this.setBlockingLoading(false);
        this.setLoading(false);
      }),
      catchError((error) => {
        this.errorHandlingService.handle(error, 'Failed to fetch documents');
        return of([]);
      }),
    );
  }

  getFileThumbnailByDocumentIdFileId(
    shareId: string,
    documentId: string,
    fileId: string,
    thumbnail: boolean = true,
    isDeleted: boolean = false,
    preview: boolean = false,
  ): Observable<Blob | null> {
    return this.shareLinkApiService
      .getSharedFileBlobByDocumentId(shareId, documentId, fileId, isDeleted, thumbnail, preview)
      .pipe(catchError(() => of(null)));
  }

  getSharedFileBlobByDocumentId(
    shareId: string,
    documentId: string,
    fileId: string,
    isDeleted: boolean = false,
    thumbnail: boolean = false,
    preview: boolean = false,
    currentLanguage: string = 'en',
  ): Observable<Blob | null> {
    this.setLoadingDetails(true);

    const requestKey = `auth_${shareId}_${documentId}_${fileId}_${isDeleted}_${thumbnail}_${preview}_${currentLanguage}`;
    const activeRequest = this.fileBlobRequestCache.get(requestKey);
    if (activeRequest) {
      return activeRequest;
    }

    const request$ = this.shareLinkApiService
      .getSharedFileBlobByDocumentId(
        shareId,
        documentId,
        fileId,
        isDeleted,
        thumbnail,
        preview,
        currentLanguage,
      )
      .pipe(
        catchError(() => of(null)),
        finalize(() => {
          this.fileBlobRequestCache.delete(requestKey);
          this.setLoadingDetails(false);
        }),
        shareReplay(1),
      );

    this.fileBlobRequestCache.set(requestKey, request$);
    return request$;
  }

  getSharedDocumentsCount(shareId: string, filterOptions?: {}): Observable<number> {
    return this.shareLinkApiService.getSharedDocumentsCount(shareId, filterOptions).pipe(
      tap((count: number) => {
        this.documentCountSubject.next(count);
      }),
      catchError(() => {
        this.setBlockingLoading(false);
        this.setLoading(false);
        return of(0);
      }),
    );
  }

  // ------- Endpoints for public share links anonymous user-------//
  getSharedExtendedMetadataPublic(
    customerId: string,
    shareId: string,
    documentExtendedRequest: IDocumentExtendedRequest,
  ): Observable<IDocumentExtendedResponse[]> {
    return this.shareLinkApiService
      .getSharedExtendedMetadataPublic(customerId, shareId, documentExtendedRequest)
      .pipe(
        tap((documents: IDocumentExtendedResponse[]) => {
          this.documentsExtendedSubject.next(documents);
        }),
        finalize(() => {
          this.setBlockingLoading(false);
          this.setLoading(false);
        }),
        catchError((error) => {
          this.errorHandlingService.handle(error, 'Failed to fetch documents');
          this.setBlockingLoading(false);
          this.setLoading(false);
          this.documentsExtendedSubject.next([]);
          return of([]);
        }),
      );
  }

  getSharedDocumentsMetadataByIdsPublic(
    customerId: string,
    shareId: string,
    documentIds: string[],
  ): Observable<IDocument[]> {
    return this.shareLinkApiService
      .getSharedDocumentsMetadataByIdsPublic(customerId, shareId, documentIds)
      .pipe(
        finalize(() => {
          this.setBlockingLoading(false);
          this.setLoading(false);
        }),
        catchError((error) => {
          this.errorHandlingService.handle(error, 'Failed to fetch documents');
          return of([]);
        }),
      );
  }

  getFileThumbnailByDocumentIdFileIdPublic(
    customerId: string,
    shareId: string,
    documentId: string,
    fileId: string,
    thumbnail: boolean = true,
    isDeleted: boolean = false,
    preview: boolean = false,
  ): Observable<Blob | null> {
    return this.shareLinkApiService
      .getSharedFileBlobByDocumentIdPublic(
        customerId,
        shareId,
        documentId,
        fileId,
        isDeleted,
        thumbnail,
        preview,
      )
      .pipe(catchError(() => of(null)));
  }

  getSharedFileBlobByDocumentIdPublic(
    customerId: string,
    shareId: string,
    documentId: string,
    fileId: string,
    isDeleted: boolean = false,
    thumbnail: boolean = false,
    preview: boolean = false,
    currentLanguage: string = 'en',
  ): Observable<Blob | null> {
    this.setLoadingDetails(true);

    const requestKey = `public_${customerId}_${shareId}_${documentId}_${fileId}_${isDeleted}_${thumbnail}_${preview}_${currentLanguage}`;
    const activeRequest = this.fileBlobRequestCache.get(requestKey);
    if (activeRequest) {
      return activeRequest;
    }

    const request$ = this.shareLinkApiService
      .getSharedFileBlobByDocumentIdPublic(
        customerId,
        shareId,
        documentId,
        fileId,
        isDeleted,
        thumbnail,
        preview,
        currentLanguage,
      )
      .pipe(
        catchError(() => of(null)),
        finalize(() => {
          this.fileBlobRequestCache.delete(requestKey);
          this.setLoadingDetails(false);
        }),
        shareReplay(1),
      );

    this.fileBlobRequestCache.set(requestKey, request$);
    return request$;
  }

  getSharedDocumentsCountPublic(
    customerId: string,
    shareId: string,
    filterOptions?: {},
  ): Observable<number> {
    return this.shareLinkApiService
      .getSharedDocumentsCountPublic(customerId, shareId, filterOptions)
      .pipe(
        tap((count: number) => {
          this.documentCountSubject.next(count);
        }),
        catchError((error) => {
          this.setBlockingLoading(false);
          this.setLoading(false);
          return of(0);
        }),
      );
  }

  sendEmailWithShareLink(shareLink: string, subject: string, body: string): void {
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(`${body}`);

    setTimeout(() => {
      window.open(`mailto:?subject=${encodedSubject}&body=${encodedBody}`);
    }, 200);
  }
}
